import { Button } from 'antd';
import React, { FC, useRef, useState } from 'react';

import CheckIcon from 'components/Product/_icons/CheckIcon.svg';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Language, Product } from 'types/schema';
import media from 'utils/mediaStyle';
import utilMessages from 'utils/messages';
import KamereoLogo from './kamereoLogo.png';
import { Navigation, Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Image } from 'components/MightFavoriteProductsWidget/Image';
import { getImageByCategory } from 'containers/MarketPage/ImageByCategory';
import { FrameImage } from 'components/Product/ProductImagesModal';
import { Viewport } from 'components/ViewportProvider';
import arrow from './arrow.svg';
import formatMoney from '../../utils/formatMoney';
import { RouteComponentProps, RouteProps, withRouter } from 'react-router-dom';
import generateProductSlug from 'utils/generateProductSlug';
import calculateVAT from 'utils/calculateVAT';

interface IStateProps {
  products: Product[];
}

const KamereoPromotionWrapper = styled.div`
  display: flex;
  background: #ffffff;
  border-radius: 0px;
  border: 1px solid #eaeaeb;
  position: relative;
  margin: 24px 0 -8px;
  justify-content: space-between;
  padding: 38px 16px 12px;
  gap: 50px;

  ${media.lg`
    padding: 24px 100px;
    border-radius: 8px;
    margin: 24px 0 0px;
  `}
`;

const PromotionCornerTitle = styled.div`
  background: #f38a3e;
  padding: 6px 12px;
  display: flex;
  gap: 4px;
  width: fit-content;
  border-radius: 8px 0 12px 0;
  position: absolute;
  top: 0;
  left: 0;
  @media (max-width: 576px) {
    border-radius: 0px 0 12px 0;
  }
`;

const PromotionCornerTitleText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
`;

const KamereoLogoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 1023px) {
    display: none;
  }
`;

const KamereoLogoImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
`;

const KamereoLogoText = styled.span`
  margin-top: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #343538;
  line-height: 20px;
`;

const KamereoLogoButton = styled(Button)`
  height: 40px;
  padding: 12px;
  border-radius: 8px;
  color: #43a047;
  border-color: #43a047;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  background: transparent;
  margin-top: 22px;
  cursor: pointer;
  &:hover {
    background: #f0f9f1 !important;
  }
  &:active {
    border-color: #3c9041 !important;
    color: #3c9041 !important;
  }
  ${media.md`
    font-size: 16px;
    `}
`;

const SwiperSlideElement = styled(SwiperSlide)`
  cursor: pointer;
  width: 160px !important;
  @media (max-width: 767px) {
    width: 78px !important;
  }
`;

const SwiperElementWrapper = styled.div`
  position: relative;
  @media (max-width: 1023px) {
    overflow: hidden;
  }
`;

const SwiperElement = styled(Swiper)`
  max-width: 712px;
  width: 100%;
  .swiper-button-next,
  .swiper-button-prev {
    display: none;
  }
  @media (max-width: 1023px) {
    max-width: unset;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  // border: 1px solid #eaeaeb;
  border-radius: 6px;
  overflow: hidden;
  width: 160px;
  height: 160px;
  @media (max-width: 767px) {
    width: 78px;
    height: 78px;
  }
`;

const NavigationButton = styled.div`
  height: 30px;
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 80px;
  border: 1px solid #d5d7d5;
  cursor: pointer;
  &:hover {
    background: #f0f9f1 !important;
  }
  &:active {
    background: #d4edd5 !important;
  }
  position: absolute;
  top: 35%;
  transform: translateY(-35%);
  left: -42px;
  z-index: 10;
  ${({ right }) =>
    right &&
    `
    right: -42px;
    left: unset;
  `}
`;

const ProductPriceValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: #3c9041;
  margin-top: 12px;
  display: flex;
  justify-content: center;
  @media (max-width: 767px) {
    font-size: 12px;
    margin-top: 4px;
  }
`;

const NavigationButtonIcon = styled(SVGIcon)`
  ${({ disabled }) => disabled && `filter: opacity(0.5);`}
`;

const KamereoPromotionProducts: FC<IStateProps & RouteProps & RouteComponentProps> = ({ products, history }) => {
  const lang = localStorage.getItem('lang') || Language.Vi;
  const swiperRef: any = useRef();
  const [showNext, setShowNext] = useState(true);
  const [showPrev, setShowPrev] = useState(false);

  const frameTagImage = (kamereoProduct) => {
    return lang === 'vi' ? kamereoProduct.tagImageVn : kamereoProduct.tagImageEn || '';
  };
  const generateImage = (product) => {
    return product?.category && !product?.imageUrl ? getImageByCategory(product?.category) : product?.imageUrl;
  };

  return (
    <KamereoPromotionWrapper>
      <PromotionCornerTitle>
        <SVGIcon src={CheckIcon} />
        <PromotionCornerTitleText>
          <FormattedMessage {...utilMessages.promotionKamereo} />
        </PromotionCornerTitleText>
      </PromotionCornerTitle>
      <KamereoLogoWrapper>
        <KamereoLogoImage src={KamereoLogo} />
        <KamereoLogoText>Kamereo</KamereoLogoText>
        <KamereoLogoButton onClick={() => history.push('/thuong-hieu-kamereo')}>
          <FormattedMessage {...utilMessages.viewKamereoLabel} />
        </KamereoLogoButton>
      </KamereoLogoWrapper>
      <Viewport.Consumer>
        {({ width }) => {
          const isMobile = width < 768;
          return (
            <SwiperElementWrapper>
              <SwiperElement
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
                slidesPerView={'auto'}
                spaceBetween={isMobile ? 16 : 24}
                navigation={products.length > 4}
                modules={[Navigation, Mousewheel]}
                mousewheel={{ forceToAxis: true }}
                simulateTouch={true}
                className="mySwiper"
                slidesPerGroup={2}
                onReachEnd={() => {
                  setShowNext(false);
                  setShowPrev(true);
                }}
                onReachBeginning={() => {
                  setShowPrev(false);
                  setShowNext(true);
                }}
                onSlideChange={(swiper) => {
                  setShowPrev(!swiper.isBeginning);
                  setShowNext(!swiper.isEnd);
                }}
              >
                {products.map((product: Product, index: number) => (
                  <SwiperSlideElement
                    key={product?.id}
                    isMobile={isMobile}
                    onClick={() =>
                      history.push(
                        `/products/${generateProductSlug(product?.name, product?.supplierInternalCode, lang)}`,
                      )
                    }
                  >
                    <ImageContainer>
                      <Image src={generateImage(product)} alt={String(name)} size={isMobile ? 78 : 160} noBorder />
                      {!!frameTagImage(product) && <FrameImage src={frameTagImage(product)} />}
                    </ImageContainer>
                    <ProductPriceValue>
                      {formatMoney(
                        calculateVAT(product.priceV2.price, product.priceV2.vat, product.priceV2.vatIncluded).beforeVat,
                      )}
                      đ
                    </ProductPriceValue>
                  </SwiperSlideElement>
                ))}
              </SwiperElement>
              {!isMobile && products.length > 4 && (
                <>
                  {showPrev && (
                    <NavigationButton
                      onClick={() => {
                        swiperRef?.current?.slidePrev();
                      }}
                    >
                      <NavigationButtonIcon src={arrow} />
                    </NavigationButton>
                  )}
                  {showNext && (
                    <NavigationButton
                      onClick={() => {
                        swiperRef?.current?.slideNext();
                      }}
                      style={{ transform: 'rotate(180deg)' }}
                      right
                    >
                      <NavigationButtonIcon src={arrow} />
                    </NavigationButton>
                  )}
                </>
              )}
            </SwiperElementWrapper>
          );
        }}
      </Viewport.Consumer>
    </KamereoPromotionWrapper>
  );
};

export default withRouter(KamereoPromotionProducts);
